import React, { useState } from 'react'
import { Modal, Radio, Checkbox } from 'antd'
import { CSVLink } from 'react-csv'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import Cancel from 'assets/Images/close.svg'
import moment from 'moment'

const ALL_COLUMNS = [
  { label: 'Family ID', key: 'familyId' },
  { label: 'Family Name', key: 'family' },
  { label: 'Family Status', key: 'familyStatus' },
  { label: 'Member ID', key: 'memberId' },
  { label: 'Member Type', key: 'memberType' },
  { label: 'Relation', key: 'relation' },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  { label: 'Email', key: 'email' },
  { label: 'Phone', key: 'phone' },
  { label: 'Date of Birth', key: 'dob' },
  { label: 'Gender', key: 'gender' },
  { label: 'School', key: 'school' },
  { label: 'Grade', key: 'grade' },
  { label: 'Allergies', key: 'allergies' },
  { label: 'Medical Notes', key: 'medicalNotes' },
  { label: 'Address', key: 'address' },
  { label: 'Member Status', key: 'memberStatus' },
]

const formatAllergies = (raw: any): string => {
  if (!raw) return ''
  if (Array.isArray(raw)) return raw.map((a: any) => a?.name || a).filter(Boolean).join(', ')
  return String(raw)
}

const formatAddress = (addr: any): string => {
  if (!addr) return ''
  const parts = [addr.apartment, addr.street, addr.city, addr.state, addr.zip, addr.country]
  return parts.filter(Boolean).join(', ')
}

const buildRow = (familyItem: any, memberType: string, member: any, extraFields: any) => ({
  familyId: familyItem?._id || '',
  family: familyItem?.name || '',
  familyStatus: familyItem?.status || '',
  memberId: member?._id || member?.id || '',
  memberType,
  relation: member?.relation?.name || (memberType === 'Kid' ? 'Kid' : ''),
  firstName: member?.firstName || '',
  lastName: member?.lastName || '',
  email: member?.email || '',
  phone: member?.phone?.number || '',
  memberStatus: member?.status || '',
  ...extraFields,
})

const ExportModalFamily = ({ visible, onClose, data }) => {
  const [fileFormat, setFileFormat] = useState('csv')
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    ALL_COLUMNS.map(c => c.label),
  )

  const csvData = (data || [])
    .flatMap((item: any) => [
      ...(item.kids || []).map((kid: any) =>
        buildRow(item, 'Kid', kid, {
          dob: kid?.dob ? moment(kid.dob).format('MMM DD, YYYY') : '',
          gender: kid?.gender || '',
          school: kid?.school || '',
          grade: kid?.grade?.name || '',
          allergies: formatAllergies(kid?.allergies),
          medicalNotes: kid?.medicalNotes || '',
          address: '',
        }),
      ),
      ...(item.guardians || []).map((guardian: any) =>
        buildRow(item, 'Guardian', guardian, {
          dob: guardian?.dob ? moment(guardian.dob).format('MMM DD, YYYY') : '',
          gender: guardian?.gender || '',
          school: '',
          grade: '',
          allergies: formatAllergies(guardian?.allergies),
          medicalNotes: guardian?.medicalNotes || '',
          address: formatAddress(guardian?.address),
        }),
      ),
      ...(item.adults || []).map((adult: any) =>
        buildRow(item, 'Adult', adult, {
          dob: adult?.dob ? moment(adult.dob).format('MMM DD, YYYY') : '',
          gender: adult?.gender || '',
          school: '',
          grade: '',
          allergies: formatAllergies(adult?.allergies),
          medicalNotes: adult?.medicalNotes || '',
          address: formatAddress(adult?.address),
        }),
      ),
    ])

  const activeHeaders = ALL_COLUMNS.filter(h => selectedColumns.includes(h.label))

  const handleFormatChange = (e: any) => setFileFormat(e.target.value)

  const handleColumnChange = (checked: boolean, label: string) => {
    setSelectedColumns(prev =>
      checked ? [...prev, label] : prev.filter(col => col !== label),
    )
  }

  const handleExport = () => {
    const filteredData = csvData.map(row => {
      const out: any = {}
      activeHeaders.forEach(h => { out[h.key] = row[h.key] })
      return out
    })

    if (fileFormat === 'csv') {
      document.getElementById('csv-download-link')?.click()
    } else if (fileFormat === 'excel') {
      const ws = XLSX.utils.json_to_sheet(filteredData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Families')
      XLSX.writeFile(wb, 'Family_Export.xlsx')
    } else if (fileFormat === 'pdf') {
      const doc = new jsPDF({ orientation: 'landscape' })
      ;(doc as any).autoTable({
        head: [activeHeaders.map(h => h.label)],
        body: filteredData.map(row => activeHeaders.map(h => row[h.key])),
        styles: { fontSize: 7 },
      })
      doc.save('Family_Export.pdf')
    }
    onClose()
  }

  return (
    <Modal open={visible} footer={false} width={560}>
      <div className="popup-header">
        <h2>Export Family Data</h2>
        <img src={Cancel} alt="close" onClick={onClose} style={{ cursor: 'pointer' }} />
      </div>

      <div className="formatText mt-10px">Format</div>
      <Radio.Group onChange={handleFormatChange} value={fileFormat}>
        <Radio value="csv">CSV</Radio>
        <Radio value="excel">Excel</Radio>
        <Radio value="pdf">PDF</Radio>
      </Radio.Group>

      <div className="radioText mt-15px">Select columns to export</div>
      <div className="checkbox-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginTop: 8 }}>
        {ALL_COLUMNS.map(({ label }) => (
          <Checkbox
            key={label}
            checked={selectedColumns.includes(label)}
            onChange={e => handleColumnChange(e.target.checked, label)}
          >
            {label}
          </Checkbox>
        ))}
      </div>

      <div className="flex justify-end w-full mt-40px">
        <button
          className="prim-sm-btn mr-30px"
          style={{ background: '#EBEDF1', color: 'black' }}
          onClick={onClose}
        >
          Cancel
        </button>
        <button className="prim-sm-btn" onClick={handleExport}>
          Export
        </button>
      </div>

      <CSVLink
        data={csvData}
        headers={activeHeaders}
        filename="Family_Export.csv"
        id="csv-download-link"
        style={{ display: 'none' }}
      />
    </Modal>
  )
}

export default ExportModalFamily
